import React, { useEffect, useMemo, useRef, useState } from 'react';
import GalleryImage from './GalleryImage';
import {
  ImageSlider,
  VehicleBasic,
  useServicesContext,
  getRelatedProduct,
  matchesKeyForVtp,
  setSelectedVehicle,
  Picture,
  DynamicComponent,
} from '@oneaudi/vtp-shared';
import { Pagination } from '@oneaudi/unified-web-components';
import { Container, StyledPagination, Spacing } from './Gallery.styles';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { trackDetailsPageNav, trackImageChange } from '../../../tracking/tilesTracking';
import styled from 'styled-components';

export interface GalleryProps {
  vehicle: VehicleBasic;
  detailsPageLink: string;
  isListView: boolean;
  focusRef?: React.Ref<HTMLAnchorElement>;
}

const ImageSliderWrapper = styled.a`
  display: block;
`;

const Gallery: React.FC<GalleryProps> = ({ vehicle, detailsPageLink, isListView, focusRef }) => {
  const [currentSlideIndex, setSlideIndex] = useState(0);
  const [trackSwipe, setTrackSwipe] = useState(false);

  const fallbackImage = useMemo(() => {
    return vehicle.fallbackPictures?.find((picture) => picture.key === 'render_4x3');
  }, [vehicle.fallbackPictures]);

  const pictures = useMemo<Picture[]>(() => {
    const result: Picture[] = [];
    const dealerImages = vehicle.pictures?.filter((picture) =>
      picture.originalKey.startsWith('dealer-images')
    );

    const tilesImages = vehicle.tilesPictures;
    const hasVtp4x3m1c = tilesImages?.some((image) => image.key?.startsWith('vtp4x3m1c'));

    const renderImages =
      tilesImages?.filter((image) => matchesKeyForVtp(image.key, hasVtp4x3m1c)) || [];

    const showDealerImages = !vehicle.nationWideSelling && dealerImages && dealerImages.length > 0;
    const showRenderImages =
      vehicle.type !== 'U' && !vehicle.hideRenderImages && renderImages && renderImages.length > 0;

    if (showRenderImages) {
      result.push(...renderImages);
    }

    if (showDealerImages) {
      result.push(...dealerImages);
    }

    if (result.length) {
      return result;
    }

    if (renderImages?.length) {
      return renderImages;
    }

    return [fallbackImage!];
  }, [vehicle.pictures, fallbackImage]);

  const relatedProduct = getRelatedProduct(vehicle);
  const trackingService = useServicesContext().getAdditionalService(
    'audi-tracking-service'
  ) as TrackingServiceV2;

  const inputRef = useRef<HTMLDivElement>(null);
  const trackSlideUpdate = (
    slide: number,
    elementName: 'arrow' | 'image' | 'other',
    actionType: 'swipe' | 'click' | 'automatic',
    clickID: string
  ) => {
    const position =
      currentSlideIndex < slide
        ? `${currentSlideIndex + 1}~${currentSlideIndex + 2}`
        : `${currentSlideIndex + 1}~${currentSlideIndex}`;
    trackImageChange(
      trackingService,
      elementName,
      `${currentSlideIndex + 1}`,
      position,
      relatedProduct,
      actionType,
      clickID
    );
  };

  const updateSlideAndTrack = (slide: number) => {
    setSlideIndex(slide);
    if (trackSwipe && slide !== currentSlideIndex) {
      trackSlideUpdate(slide, 'image', 'swipe', inputRef.current?.getAttribute('id')!);
    }
  };
  useEffect(() => {
    setTrackSwipe(true);
  }, [trackSwipe]);

  return (
    <Container>
      {pictures.length > 1 && (
        <>
          <ImageSliderWrapper
            ref={focusRef}
            href={detailsPageLink}
            onClick={(evt: any) => {
              setSelectedVehicle(vehicle.id);
              if (evt.target.nodeName === 'IMG') {
                trackDetailsPageNav(trackingService, '', detailsPageLink, 'image');
              }
            }}
          >
            <ImageSlider
              isPLP
              disableButtonNavigation
              updatedSlide={(slide: number) => {
                updateSlideAndTrack(slide);
              }}
              slide={currentSlideIndex}
              data-testid="imageSlider"
            >
              {pictures.map((picture, index) => (
                <GalleryImage
                  vehicle={vehicle}
                  picture={picture!}
                  key={picture!.key}
                  active={index === currentSlideIndex}
                  renderPicture={index >= currentSlideIndex - 1 && index <= currentSlideIndex + 1}
                />
              ))}
            </ImageSlider>
          </ImageSliderWrapper>
          <StyledPagination isListView={isListView}>
            <div
              ref={inputRef}
              id={`gallery-pagination-${currentSlideIndex + 1}`}
              data-testid="pagination"
            >
              <Pagination
                current={currentSlideIndex + 1}
                itemCount={pictures.length}
                decrementElement={{
                  'aria-label': 'Previous image',
                  onClick: () => {
                    if (currentSlideIndex > 0) {
                      const newSlide = currentSlideIndex;
                      setSlideIndex(newSlide - 1);
                      trackSlideUpdate(newSlide, 'arrow', 'click', inputRef.current?.id || '');
                    }
                  },
                }}
                incrementElement={{
                  'aria-label': 'Next image',
                  onClick: () => {
                    if (currentSlideIndex < pictures.length - 1) {
                      const newSlide = currentSlideIndex + 2;
                      setSlideIndex(newSlide - 1);
                      trackSlideUpdate(newSlide, 'arrow', 'click', inputRef.current?.id || '');
                    }
                  },
                }}
                variant="compact"
              />
            </div>
          </StyledPagination>
        </>
      )}
      {pictures.length === 1 && (
        <>
          <a
            ref={focusRef}
            href={detailsPageLink}
            onClick={(evt: any) => {
              setSelectedVehicle(vehicle.id);
              if (evt.target.nodeName === 'IMG') {
                trackDetailsPageNav(trackingService, '', detailsPageLink, 'image');
              }
            }}
          >
            <GalleryImage vehicle={vehicle} picture={pictures[0]!} isSinglePicture />
          </a>
          <DynamicComponent
            legacy={<StyledPagination isListView={isListView} pictureLength={pictures.length} />}
            alpha={<Spacing />}
          />
        </>
      )}
      {pictures.length === 0 && fallbackImage && (
        <>
          <a
            ref={focusRef}
            href={detailsPageLink}
            onClick={(evt: any) => {
              setSelectedVehicle(vehicle.id);
              if (evt.target.nodeName === 'IMG') {
                trackDetailsPageNav(trackingService, '', detailsPageLink, 'image');
              }
            }}
          >
            <GalleryImage vehicle={vehicle} picture={fallbackImage} isSinglePicture />
          </a>
          <DynamicComponent
            legacy={<StyledPagination isListView={isListView} pictureLength={pictures.length} />}
            alpha={<Spacing />}
          />
        </>
      )}
    </Container>
  );
};

export default Gallery;
